import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MOTION, STATUS, SURFACE } from "../../theme/tokens";
import { resolveCallInTime } from "../../utils/callIns";
import type { CalledOffShift } from "../../utils/models";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";

type Props = {
	/** The call-ins this marker stands for — one day's worth for a calendar cell. Renders nothing when empty. */
	callIns: CalledOffShift[];
	/** Badge diameter in px. Defaults to the compact size that fits a calendar cell corner. */
	size?: number;
	dataTestId?: string;
	/** Placement overrides — the caller owns where the badge sits, not how it looks. */
	sx?: SxProps<Theme>;
};

const DEFAULT_SIZE_PX = 24;
/**
 * The mark reads as "this shift was cancelled" first, "by a phone call" second: the calendar-with-an-X
 * (the cancellation) is the large, primary glyph; the phone (the call) tucks into its top-right corner
 * — the empty space a centered glyph always leaves in a circle's corner — as a SECOND STROKE, not a
 * second shape.
 *
 * Three earlier passes all wrapped the phone in its own disc (a `Badge` chip: solid-orange, then a
 * white plate + ring) and all read as clutter — a small circle stacked on a circle inside a circle is
 * three competing silhouettes for one 24px mark to carry. Dropping the disc entirely and separating the
 * two glyphs with a `drop-shadow` KEYLINE (a thin light halo hugging the phone's own outline, stacked a
 * few times for a crisp edge — a soft blur alone read too fuzzy at this size) gets the same "this glyph
 * sits in front" separation a badge chip was reaching for, without ever drawing a shape that isn't the
 * icon itself. Same tonal family as before: one ink color (`STATUS.WARNING.dark`) for both glyphs, on
 * the shared soft-wash container — the halo is the only new value, and it derives from `SURFACE` (the
 * container's own white/paper ground) so it recedes into whatever backs the marker instead of reading
 * as its own painted layer.
 */
const PRIMARY_GLYPH_RATIO = 0.8;
/** The phone glyph riding in the primary calendar's corner, relative to `size`. */
const SECONDARY_GLYPH_RATIO = 0.48;
/** How far the secondary glyph's center sits from the primary's corner, as a fraction of `size`. */
const SECONDARY_OFFSET_RATIO = 0.06;
/** Halo keyline blur radius (px) — kept tight so it stays crisp rather than a soft glow at badge scale. */
const HALO_BLUR_PX = 1;
/** `drop-shadow` accepts a space-separated list — stacking this many identical shadows compounds the
 *  blur into a defined keyline instead of the faint, barely-there edge a single pass leaves. */
const HALO_LAYERS = 4;
const HALO_FILTER = Array.from({ length: HALO_LAYERS }, () => `drop-shadow(0 0 ${HALO_BLUR_PX}px ${SURFACE})`).join(" ");

/**
 * One-shot arrival pop.
 *
 * Two animations on one element, deliberately: the badge itself scales up through an overshoot and
 * settles, while a `box-shadow` ring blooms out and fades. The ring is what makes a marker this small in
 * a calendar-cell corner actually catch the eye — a bare scale on something that small is easy to miss
 * next to everything else moving when a shift is removed.
 *
 * Transform/opacity for the scale (GPU, no layout thrash), never from `scale(0)`. The ring animates
 * `box-shadow`, which does paint — acceptable for one small element for under half a second, and there
 * is no transform-only way to draw an expanding ring outside the element's own box. Both are dropped
 * entirely under reduced motion.
 */
const POP_SX: SxProps<Theme> = {
	"@keyframes callInMarkerPop": {
		"0%": { opacity: 0, transform: "scale(0.4)" },
		"55%": { opacity: 1, transform: "scale(1.18)" },
		"75%": { transform: "scale(0.96)" },
		"100%": { opacity: 1, transform: "scale(1)" },
	},
	"@keyframes callInMarkerRing": {
		"0%": { boxShadow: `0 0 0 0 ${alpha(STATUS.WARNING.main, 0.55)}` },
		"100%": { boxShadow: `0 0 0 10px ${alpha(STATUS.WARNING.main, 0)}` },
	},
	animation: `callInMarkerPop ${MOTION.DURATION.POP}ms ${MOTION.EASE_OUT}, callInMarkerRing ${MOTION.DURATION.PULSE}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * Markers whose pop has actually run to completion.
 *
 * The profile calendar is virtualized (`react-window` unmounts off-screen week rows), so without this
 * every marker would re-pop each time its week scrolled back into view — a cascade of bouncing badges
 * on every scroll, which reads as a glitch rather than an arrival. Keyed by the call-in ids the marker
 * stands for, so a day that GAINS a call-in pops again while a day merely scrolling past does not.
 *
 * Recorded once the pop has had time to RUN rather than on mount, so the set means "the user actually
 * saw this arrive". A marker whose row scrolls away after 80ms never records, and gets its pop properly
 * the next time it comes into view — where a mount-time write would have silently spent it.
 *
 * A timer rather than `animationend`: jsdom implements no `AnimationEvent`, so React never registers
 * that listener and the behavior would be untestable. The timer is deterministic under fake timers.
 *
 * Session-scoped and tiny (one short string per call-in the user actually looked at); deliberately not
 * cleared, since "already seen" should survive navigating away and back.
 */
const POPPED_MARKERS = new Set<string>();

/**
 * The phone-and-cancelled-calendar marker that says "this shift was called in" — the one place the app
 * draws that fact, so the profile calendar, the day view, and anything else added later all read
 * identically.
 *
 * Warning-toned rather than error-toned on purpose: a call-in is a fact the manager needs to see, not a
 * failure they have to act on. The tooltip carries what the glyph can't — which shift, why, and when the
 * nurse called — because a calendar cell has no room for a line of text per call-in.
 */
export const CallInIndicator: FC<Props> = ({ callIns, size = DEFAULT_SIZE_PX, dataTestId, sx }) => {
	const { t } = useTranslation();
	const markerKey = callIns.map((callIn) => callIn.id).join("|");
	// Decided in state (not derived inline) so the very first painted frame already carries the
	// animation — computing it in an effect would show one un-animated frame first, and (see
	// `POPPED_MARKERS`) would make StrictMode's remount suppress the pop outright.
	//
	// Recomputed synchronously during render whenever `markerKey` changes, rather than only once at
	// mount: the calendar cell does not remount `CallInIndicator` when its day's call-ins grow in place
	// (no `key` keyed to the marker), so a day going from `[first]` to `[first, second]` while staying
	// mounted must re-derive `shouldPop` for the new key here, not freeze the value the first mount saw.
	// This is React's "adjusting state during rendering" pattern, not a `useEffect`, for the same
	// no-un-animated-frame reason as the initializer.
	const [shouldPop, setShouldPop] = useState(() => !POPPED_MARKERS.has(markerKey));
	const [lastMarkerKey, setLastMarkerKey] = useState(markerKey);
	if (markerKey !== lastMarkerKey) {
		setLastMarkerKey(markerKey);
		setShouldPop(!POPPED_MARKERS.has(markerKey));
	}

	useEffect(() => {
		// Nothing to record for an empty marker (it renders nothing) or one that never animated.
		if (!shouldPop || !markerKey) return;
		const timer = setTimeout(() => POPPED_MARKERS.add(markerKey), MOTION.DURATION.POP);
		// Scrolling the row away mid-flight cancels the record, so the interrupted arrival still gets
		// its pop the next time it comes into view.
		return () => clearTimeout(timer);
	}, [shouldPop, markerKey]);

	if (!callIns.length) return null;

	const tooltip = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
			<Typography sx={{ fontSize: "12px", fontWeight: 700 }}>{t("call.in")}</Typography>
			{callIns.map((callIn) => (
				<Box key={callIn.id} sx={{ display: "flex", flexDirection: "column" }}>
					<Typography sx={{ fontSize: "12px" }}>
						{callIn.startTime.toFormat("h:mm a")} - {callIn.endTime.toFormat("h:mm a")}
					</Typography>
					<Typography sx={{ fontSize: "11px", opacity: 0.85 }}>{callIn.message ?? t("call.in.no.reason.provided")}</Typography>
					<Typography sx={{ fontSize: "11px", opacity: 0.85 }}>
						{t("call.in.called.at", { time: resolveCallInTime(callIn).toFormat("ccc, MMM d, h:mm a") })}
					</Typography>
				</Box>
			))}
		</Box>
	);

	// A nurse working a double can call in for one shift and work the other, so a day legitimately holds
	// more than one call-in. The count is on the badge rather than tooltip-only: the whole point of the
	// marker is that the manager doesn't have to hover to know something happened.
	const showCount = callIns.length > 1;

	return (
		<InHouseTooltip title={tooltip}>
			<Box
				data-testid={dataTestId}
				aria-label={showCount ? t("call.in.count", { count: callIns.length }) : t("call.in")}
				sx={[
					...(shouldPop ? [POP_SX] : []),
					{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						gap: `${Math.round(size * 0.12)}px`,
						flexShrink: 0,
						minWidth: size,
						height: size,
						px: showCount ? `${Math.round(size * 0.16)}px` : 0,
						borderRadius: showCount ? `${size / 2}px` : "50%",
						backgroundColor: alpha(STATUS.WARNING.main, 0.16),
						color: STATUS.WARNING.dark,
					},
					...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
				]}
			>
				<Box
					sx={{
						position: "relative",
						width: `${Math.round(size * PRIMARY_GLYPH_RATIO)}px`,
						height: `${Math.round(size * PRIMARY_GLYPH_RATIO)}px`,
						flexShrink: 0,
					}}
				>
					<EventBusyOutlinedIcon sx={{ fontSize: `${Math.round(size * PRIMARY_GLYPH_RATIO)}px`, display: "block" }} />
					<PhoneOutlinedIcon
						sx={{
							position: "absolute",
							top: `${-Math.round(size * SECONDARY_OFFSET_RATIO)}px`,
							right: `${-Math.round(size * SECONDARY_OFFSET_RATIO)}px`,
							fontSize: `${Math.round(size * SECONDARY_GLYPH_RATIO)}px`,
							// The keyline halo (see const doc above) is what lets this sit "in front of" the
							// calendar glyph without a background shape of its own.
							filter: HALO_FILTER,
						}}
					/>
				</Box>
				{showCount && (
					<Box component="span" sx={{ fontSize: `${Math.round(size * 0.5)}px`, fontWeight: 700, lineHeight: 1 }}>
						{callIns.length}
					</Box>
				)}
			</Box>
		</InHouseTooltip>
	);
};
